import { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { SimpleTag } from '@/models/Tag';
import { bodyTextSmall } from '@/styles/styles';
import { PlusIcon } from '@heroicons/react/24/outline';
import XMarkIcon from '@heroicons/react/20/solid/XMarkIcon';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import MainButton from '@/components/ui/custom/buttons/MainButton';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { BaseInputProps } from '@/components/ui/custom/dataInput/BaseInput';
import Popup from '@/components/ui/custom/Popup';
import Skeleton from '@/components/ui/custom/Skeleton';

type Props = PropsWithChildren &
  BaseInputProps & {
    onDelete: (tagId: number) => void;
    tags?: SimpleTag[];
    setRef?: (ref: HTMLDivElement) => void;
    loading?: boolean;
  };

export function TagPopup(props: Props) {
  const { t } = useTranslation('translation', { keyPrefix: 'topics.tags' });
  const {
    setRef,
    className,
    children,
    tags = [],
    onDelete,
    loading,
    label,
  } = props;

  return (
    <div ref={setRef} className={twMerge('flex flex-col', className?.base)}>
      <ConditionalRenderer condition={label}>
        <label className={twMerge(bodyTextSmall, className?.label)}>
          {label}
        </label>
      </ConditionalRenderer>
      <ConditionalRenderer
        condition={!loading}
        fallback={
          <Skeleton
            testId="skeleton"
            className={twMerge(
              'w-full h-8 rounded-md bg-primary-content',
              className?.skeleton,
            )}
          />
        }
      >
        <ul
          className={twMerge(
            'flex gap-2 min-h-8 items-center flex-wrap',
            className?.ul,
          )}
        >
          {tags.map(tag => (
            <li
              key={tag.id}
              role="tag"
              className={twMerge('flex items-center gap-2', className?.li)}
              style={{
                backgroundColor: tag.color || 'var(--primary)',
                color: 'var(--base-100)',
              }}
            >
              <p className={twMerge(bodyTextSmall, className?.text)}>
                {tag.name}
              </p>
              <IconButton
                type="button"
                testId="deleteButton"
                onClick={e => {
                  e.stopPropagation();
                  onDelete(tag.id);
                }}
                icon={<XMarkIcon className="w-3.5" />}
                className={twMerge(
                  'flex items-center gap-2',
                  className?.button,
                )}
              />
            </li>
          ))}
          <li>
            <Popup
              placement="bottom-start"
              reference={
                <MainButton
                  text={t('addContent')}
                  color="custom"
                  className="font-500 !text-14 text-secondary gap-1"
                  dataTestId="addTagButton"
                  icon={
                    <PlusIcon
                      className={
                        'w-4 h-4 ease-in-out transition-all duration-200'
                      }
                      strokeWidth={3}
                    />
                  }
                />
              }
            >
              {children}
            </Popup>
          </li>
        </ul>
      </ConditionalRenderer>
    </div>
  );
}
